import { saveEvmTxIndexes } from '@pkg/database-portfolio'
import type { AccountEvm } from '@pkg/database-portfolio'
import type { Chain } from '@pkg/evm'
import type { RunToDatabase } from '../types'
import { fetchAddressData } from './fetchAddressData'

/**
 * 通常 API は取得した取引の末尾を保存し、範囲保証のある API はページ単位で再開位置を残す。
 * @param params アカウント、取得範囲、認証と DB 書込み。全範囲の取得後に同期完了にする。
 * @returns 全範囲の取得と保存が終わると完了する。失敗ページの位置は進めない。
 */
export const fetchAndSaveAddressData = async ({
  account,
  chain,
  getToken,
  toBlock,
  transactionCount,
  startTime,
  writeToDatabase,
}: {
  account: AccountEvm
  chain: Chain
  getToken?: () => Promise<string | null>
  toBlock: number
  transactionCount: number
  startTime: number
  writeToDatabase: RunToDatabase
}): Promise<void> => {
  const getPage = chain.explorer.getAddressHistoryPage
  if (getPage === undefined) {
    const data = await fetchAddressData({
      account,
      chain,
      getToken,
      toBlock,
      transactionCount,
    })
    await writeToDatabase((database) =>
      saveEvmTxIndexes({
        database,
        accountId: account.id,
        chainId: chain.id,
        internalTransactions: data.internalTransactions,
        txIndexes: data.indexes,
        startTime,
        transactionCount,
        // RPC の最新 block は Explorer の反映済み範囲を保証しないため、取引の末尾から再取得する。
      }),
    )
    return
  }
  let fromBlock = account.evmToBlock ?? 0
  while (fromBlock <= toBlock) {
    const page = await getPage({
      address: account.evmAddress,
      fromBlock,
      toBlock,
      headers:
        getToken === undefined
          ? undefined
          : async () => {
              const token = await getToken()
              return token === null
                ? undefined
                : { Authorization: `Bearer ${token}` }
            },
    })
    if (
      !Number.isSafeInteger(page.toBlock) ||
      page.toBlock < fromBlock ||
      page.toBlock > toBlock
    )
      throw new Error(
        'Explorer history page did not advance within the requested range',
      )
    await writeToDatabase((database) =>
      saveEvmTxIndexes({
        database,
        accountId: account.id,
        chainId: chain.id,
        internalTransactions: page.internalTransactions,
        txIndexes: page.indexes,
        startTime,
        transactionCount,
        scannedToBlock: page.toBlock,
        isComplete: page.toBlock === toBlock,
      }),
    )
    fromBlock = page.toBlock + 1
  }
}

/**
 * 複数アカウントの履歴走査を共有し、取得位置と履歴はアカウント別に保存する。
 * @param params 対象アカウントと nonce、チェーン、取得末尾、DB 書込み。
 * @returns 全アカウントが範囲末尾まで取得できた場合に完了する。
 */
export const fetchAndSaveAddressesData = async ({
  accounts,
  chain,
  toBlock,
  startTime,
  writeToDatabase,
}: {
  accounts: readonly { account: AccountEvm; transactionCount: number }[]
  chain: Chain
  toBlock: number
  startTime: number
  writeToDatabase: RunToDatabase
}): Promise<void> => {
  if (accounts.length === 0) return
  const getPage = chain.explorer.getAddressesHistoryPage
  if (getPage === undefined)
    throw new Error('Explorer does not support grouped address history')
  const byAddress = new Map(
    accounts.map((item) => [item.account.evmAddress.toLowerCase(), item]),
  )
  const cursors = new Map(
    accounts.map(({ account }) => [account.id, account.evmToBlock ?? 0]),
  )
  let fromBlock = Math.min(...cursors.values())
  while (fromBlock <= toBlock) {
    const page = await getPage({
      addresses: accounts.map(({ account }) => account.evmAddress),
      fromBlock,
      toBlock,
    })
    if (
      !Number.isSafeInteger(page.toBlock) ||
      page.toBlock < fromBlock ||
      page.toBlock > toBlock
    )
      throw new Error('Explorer grouped history page did not advance')
    const seen = new Set<string>()
    for (const item of page.accounts) {
      const address = item.address.toLowerCase()
      if (!byAddress.has(address) || seen.has(address))
        throw new Error(
          'Explorer returned unexpected or duplicate account history',
        )
      seen.add(address)
    }
    if (seen.size !== byAddress.size)
      throw new Error('Explorer omitted an account history')
    await writeToDatabase(async (database) => {
      for (const item of page.accounts) {
        const target = byAddress.get(item.address.toLowerCase())
        if (target === undefined) throw new Error('Account history is missing')
        const cursor = cursors.get(target.account.id) ?? 0
        if (page.toBlock < cursor) continue
        await saveEvmTxIndexes({
          database,
          accountId: target.account.id,
          chainId: chain.id,
          internalTransactions: item.internalTransactions.filter(
            (tx) => tx.blockNumber >= cursor && tx.blockNumber <= page.toBlock,
          ),
          txIndexes: item.indexes.filter(
            (tx) => tx.blockNumber >= cursor && tx.blockNumber <= page.toBlock,
          ),
          startTime,
          transactionCount: target.transactionCount,
          scannedToBlock: page.toBlock,
          isComplete: page.toBlock === toBlock,
        })
      }
    })
    for (const { account } of accounts)
      cursors.set(
        account.id,
        Math.max(cursors.get(account.id) ?? 0, page.toBlock),
      )
    fromBlock = page.toBlock + 1
  }
}
